import { track } from '../lib/analytics'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toPng } from 'html-to-image'
import { useLocation } from 'react-router-dom'
import { api, ApiError, formatApiError } from '../lib/api'
import {
  countChars,
  FEEDBACK_IMPACT_LABELS,
  FEEDBACK_MAX_CHARS,
  FEEDBACK_RESOLUTION_LABELS,
  FEEDBACK_STATUS_LABELS,
  FEEDBACK_TYPE_LABELS,
  loadDraft,
  saveDraft,
  type FeedbackImpact,
  type FeedbackType,
  type MyFeedbackRow,
} from '../lib/feedback'
import { useIsMobile } from '../lib/useIsMobile'
import { Button } from './ui/Button'

declare const __BUILD_TIMESTAMP__: number

type Tab = 'send' | 'mine'

const MSG_TOO_LONG = `Your message is over ${FEEDBACK_MAX_CHARS.toLocaleString('en-US')} characters. Please shorten it.`
const MSG_RATE_LIMIT = "You've reached the limit for feedback. Please try again later."
const MSG_SHOT_FAILED =
  "Your feedback was sent, but the screenshot didn't upload. You can try again."
const MSG_CAPTURE_FAILED = "Couldn't capture this page. Paste or upload an image instead."
const MSG_CAPTURE_BLOCKED =
  "This page shows your customers' details. Paste a cropped image instead."
const MSG_SHOT_TICK =
  "I've checked that no customer names, phone numbers or invoice details are showing."
const MAX_SHOT_BYTES = 2 * 1024 * 1024
// Pages full of customer data: whole-page capture is disabled here.
const NO_CAPTURE_ROUTES = ['/reminders', '/outstanding-contacts', '/analytics']
const MSG_FAILED = "Couldn't send your feedback. Your message is still here, so please try again."

function submitErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return MSG_RATE_LIMIT
    if (err.status === 422) {
      const detail = formatApiError(err, '')
      if (detail.startsWith('Message is too long')) return MSG_TOO_LONG
      if (detail === 'Please write a message.') return detail
    }
  }
  return MSG_FAILED
}

function normaliseViewport() {
  return `${window.innerWidth}x${window.innerHeight}`
}

export function FeedbackPanel({
  open,
  onClose,
  unreadCount,
}: {
  open: boolean
  onClose: () => void
  unreadCount: number
}) {
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const location = useLocation()
  const [tab, setTab] = useState<Tab>('send')
  const initial = loadDraft()
  const [type, setType] = useState<FeedbackType | null>(initial.type)
  const [body, setBody] = useState(initial.body)
  const [impact, setImpact] = useState<FeedbackImpact | null>(initial.impact)
  const [contactOk, setContactOk] = useState(initial.contactOk)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [shot, setShot] = useState<Blob | null>(null)
  const [shotUrl, setShotUrl] = useState<string | null>(null)
  const [shotChecked, setShotChecked] = useState(false)
  const [shotNote, setShotNote] = useState<string | null>(null)
  const [retryId, setRetryId] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [newIds, setNewIds] = useState<Set<string>>(new Set())

  const panelRef = useRef<HTMLElement>(null)
  const firstTypeRef = useRef<HTMLButtonElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const successRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    saveDraft({ type, body, impact, contactOk })
  }, [type, body, impact, contactOk])

  const mine = useQuery({
    queryKey: ['feedback-mine'],
    queryFn: () => api.get<MyFeedbackRow[]>('/api/v1/feedback'),
    enabled: open && tab === 'mine',
    staleTime: 0,
    refetchOnMount: 'always',
  })

  // Mark everything seen only AFTER the list has rendered, remembering
  // which items were unread so they keep a "New" label until the panel
  // closes.
  const seenFor = useRef<unknown>(null)
  useEffect(() => {
    if (!open || tab !== 'mine' || !mine.data || mine.isFetching) return
    if (seenFor.current === mine.data) return
    seenFor.current = mine.data
    const unread = new Set(mine.data.filter((r) => r.unread).map((r) => r.feedback_id))
    if (unread.size > 0) setNewIds((prev) => new Set([...prev, ...unread]))
    if (unreadCount > 0 || unread.size > 0) {
      api
        .post('/api/v1/feedback/seen')
        .catch(() => undefined)
        .finally(() => queryClient.invalidateQueries({ queryKey: ['feedback-unread'] }))
    }
  }, [open, tab, mine.data, mine.isFetching, unreadCount, queryClient])

  useEffect(() => {
    if (open) {
      queryClient.invalidateQueries({ queryKey: ['feedback-unread'] })
      // Focus the first control once the panel is visible.
      const t = window.setTimeout(() => firstTypeRef.current?.focus(), 0)
      return () => window.clearTimeout(t)
    }
    setNewIds(new Set())
    seenFor.current = null
    setTab('send')
  }, [open, queryClient])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        if (document.activeElement === textareaRef.current) {
          // Keep focus inside the panel so the second Escape still reaches it.
          textareaRef.current?.blur()
          panelRef.current?.focus()
        } else {
          onClose()
        }
        return
      }
      if (e.key === 'Tab' && isMobile && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), textarea, input, [href], [tabindex]:not([tabindex="-1"])',
        )
        if (focusable.length === 0) return
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    },
    [isMobile, onClose],
  )

  const setScreenshot = (blob: Blob | null) => {
    // Any replace or remove clears the tick: it must be re-confirmed for
    // the new image.
    setShotChecked(false)
    setShotNote(null)
    setShotUrl((old) => {
      if (old) URL.revokeObjectURL(old)
      return blob ? URL.createObjectURL(blob) : null
    })
    setShot(blob)
  }

  const acceptFile = (file: File | null | undefined) => {
    if (!file) return
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setShotNote('Please use a PNG, JPEG or WebP image.')
      return
    }
    if (file.size > MAX_SHOT_BYTES) {
      setShotNote('That image is over 2 MB. Please use a smaller one.')
      return
    }
    setScreenshot(file)
  }

  const onPaste = (e: React.ClipboardEvent) => {
    const file = Array.from(e.clipboardData.files).find((f) => f.type.startsWith('image/'))
    if (file) {
      e.preventDefault()
      acceptFile(file)
    }
  }

  const captureBlocked = NO_CAPTURE_ROUTES.some((r) => location.pathname.startsWith(r))

  const capturePage = async () => {
    setCapturing(true)
    setShotNote(null)
    try {
      const dataUrl = await toPng(document.body, {
        cacheBust: true,
        filter: (node) =>
          !(node instanceof HTMLElement && node.dataset.testid === 'feedback-panel'),
      })
      const blob = await (await fetch(dataUrl)).blob()
      if (blob.size > MAX_SHOT_BYTES) {
        setShotNote('That capture is over 2 MB. Paste a cropped image instead.')
      } else {
        setScreenshot(blob)
      }
    } catch {
      setShotNote(MSG_CAPTURE_FAILED)
    } finally {
      setCapturing(false)
    }
  }

  const uploadShot = async (id: string, blob: Blob) => {
    const form = new FormData()
    form.append('file', blob, 'screenshot.png')
    try {
      await api.postForm(`/api/v1/feedback/${id}/screenshot`, form)
      setRetryId(null)
      setScreenshot(null)
      return true
    } catch {
      setRetryId(id)
      setShotNote(MSG_SHOT_FAILED)
      return false
    }
  }

  const charCount = countChars(body)
  const overLimit = charCount > FEEDBACK_MAX_CHARS
  const canSend =
    type !== null &&
    body.trim().length > 0 &&
    !overLimit &&
    !sending &&
    (shot === null || shotChecked || retryId !== null)

  const selectType = (next: FeedbackType) => {
    setType(next)
    setImpact(null) // impact only applies to bugs; reset when the type changes
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSend || type === null) return
    setSending(true)
    setError(null)
    setSuccess(null)
    try {
      const result = await api.post<{ reference: string; feedback_id: string }>('/api/v1/feedback', {
        type,
        body,
        impact: type === 'bug' ? impact : null,
        contact_ok: contactOk,
        route: window.location.pathname,
        app_version: String(__BUILD_TIMESTAMP__),
        viewport: normaliseViewport(),
      })
      track('feedback_sent')
      setType(null)
      setBody('')
      setImpact(null)
      setSuccess(`Thanks, logged as ${result.reference}. Replies will show under Your feedback.`)
      if (shot) await uploadShot(result.feedback_id, shot)
      queryClient.invalidateQueries({ queryKey: ['feedback-mine'] })
      window.setTimeout(() => successRef.current?.focus(), 0)
    } catch (err) {
      setError(submitErrorMessage(err))
    } finally {
      setSending(false)
    }
  }

  const sheetClasses = isMobile
    ? 'fixed inset-0 z-50 w-full'
    : 'fixed inset-y-0 right-0 z-40 w-[420px] border-l border-border shadow-xl'

  // Not mounted while closed (hidden content would still match text
  // locators); the draft lives in this component's state and lib/feedback.
  if (!open) return null

  return (
    <aside
      ref={panelRef}
      role={isMobile ? 'dialog' : 'complementary'}
      aria-modal={isMobile ? true : undefined}
      aria-label="Feedback"
      data-testid="feedback-panel"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      className={`${sheetClasses} flex flex-col bg-card text-foreground outline-none`}
    >
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-card-title">Feedback</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close feedback"
          className="rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-muted"
        >
          Close
        </button>
      </div>

      <div role="tablist" className="flex border-b border-border text-sm">
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'send'}
          onClick={() => setTab('send')}
          className={`px-4 py-2.5 font-medium ${tab === 'send' ? 'border-b-2 border-brand-500 text-foreground' : 'text-muted-foreground'}`}
        >
          Send
        </button>
        <button
          role="tab"
          type="button"
          aria-selected={tab === 'mine'}
          onClick={() => setTab('mine')}
          className={`flex items-center gap-2 px-4 py-2.5 font-medium ${tab === 'mine' ? 'border-b-2 border-brand-500 text-foreground' : 'text-muted-foreground'}`}
        >
          Your feedback
          {unreadCount > 0 && (
            <span
              data-testid="feedback-tab-dot"
              role="img"
              aria-label="New reply"
              className="size-2 rounded-full bg-brand-500"
            />
          )}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4">
        {tab === 'send' ? (
          <form onSubmit={submit} className="space-y-4" noValidate>
            <p
              ref={successRef}
              tabIndex={-1}
              aria-live="polite"
              className={success ? 'rounded-md bg-muted px-3 py-2 text-sm outline-none' : 'sr-only'}
            >
              {success ?? ''}
            </p>

            <fieldset>
              <legend className="mb-1.5 text-ui-label">What is this about?</legend>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(FEEDBACK_TYPE_LABELS) as FeedbackType[]).map((t, i) => (
                  <button
                    key={t}
                    type="button"
                    ref={i === 0 ? firstTypeRef : undefined}
                    aria-pressed={type === t}
                    onClick={() => selectType(t)}
                    className={`min-h-11 rounded-md border px-3 py-2 text-sm ${
                      type === t
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-input hover:bg-muted'
                    }`}
                  >
                    {FEEDBACK_TYPE_LABELS[t]}
                  </button>
                ))}
              </div>
            </fieldset>

            <div>
              <label htmlFor="feedback-body" className="mb-1 block text-ui-label">
                Your message
              </label>
              <textarea
                id="feedback-body"
                ref={textareaRef}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="What were you trying to do, and what happened?"
                rows={6}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50"
              />
              <div
                className={`mt-1 text-right text-xs ${overLimit ? 'text-danger-600' : 'text-muted-foreground'}`}
              >
                {charCount.toLocaleString('en-US')}/{FEEDBACK_MAX_CHARS.toLocaleString('en-US')}
              </div>
            </div>

            {type === 'bug' && (
              <fieldset>
                <legend className="mb-1.5 text-ui-label">How much does this get in your way?</legend>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(FEEDBACK_IMPACT_LABELS) as FeedbackImpact[]).map((i) => (
                    <button
                      key={i}
                      type="button"
                      aria-pressed={impact === i}
                      onClick={() => setImpact(impact === i ? null : i)}
                      className={`min-h-11 rounded-md border px-3 py-2 text-sm ${
                        impact === i
                          ? 'border-foreground bg-foreground text-background'
                          : 'border-input hover:bg-muted'
                      }`}
                    >
                      {FEEDBACK_IMPACT_LABELS[i]}
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            <div className="space-y-2">
              <span className="block text-ui-label">
                Screenshot <span className="font-normal text-muted-foreground">(optional)</span>
              </span>
              {shotUrl ? (
                <div className="flex items-start gap-3 rounded-md border border-dashed border-input bg-muted p-2">
                  <img
                    src={shotUrl}
                    alt="Screenshot preview"
                    className="h-20 w-28 flex-none border border-input object-cover"
                  />
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">Attached. Check it before you send.</p>
                    <button
                      type="button"
                      onClick={() => setScreenshot(null)}
                      className="underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Paste or upload
                  </Button>
                  {!isMobile && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={captureBlocked || capturing}
                      onClick={capturePage}
                    >
                      {capturing ? 'Capturing…' : 'Capture this page'}
                    </Button>
                  )}
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="sr-only"
                aria-label="Upload a screenshot"
                data-testid="feedback-file-input"
                onChange={(e) => {
                  acceptFile(e.target.files?.[0])
                  e.target.value = ''
                }}
              />
              {!shotUrl && !isMobile && captureBlocked && (
                <p className="text-xs text-muted-foreground">{MSG_CAPTURE_BLOCKED}</p>
              )}
              {!shotUrl && (
                <p className="text-xs text-muted-foreground">
                  You can also paste an image with Ctrl+V.
                </p>
              )}
              {shot && retryId === null && (
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={shotChecked}
                    onChange={(e) => setShotChecked(e.target.checked)}
                    className="mt-0.5 size-4"
                  />
                  {MSG_SHOT_TICK}
                </label>
              )}
              {shotNote && (
                <p role="alert" className="text-sm text-danger-600">
                  {shotNote}
                </p>
              )}
              {retryId && shot && (
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => uploadShot(retryId, shot)}
                >
                  Try the screenshot again
                </Button>
              )}
            </div>

            <p className="border-l-4 border-foreground bg-muted px-3 py-2 text-sm">
              Please don't include customer names, phone numbers or invoice details. Describe the
              problem instead.
            </p>

            <div className="space-y-1 text-sm text-muted-foreground">
              <p>We'll attach the page you're on and your browser.</p>
              <p>We keep this with your email so we can reply. Our team sees it in Slack.</p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={contactOk}
                onChange={(e) => setContactOk(e.target.checked)}
                className="mt-0.5 size-4"
              />
              OK for us to follow up with you by email
            </label>

            {error && (
              <p role="alert" className="text-sm text-danger-600">
                {error}
              </p>
            )}

            <Button type="submit" disabled={!canSend}>
              {sending ? 'Sending…' : 'Send feedback'}
            </Button>
          </form>
        ) : (
          <MineList
            rows={mine.data}
            loading={mine.isLoading}
            failed={mine.isError}
            newIds={newIds}
          />
        )}
      </div>
    </aside>
  )
}

function MineList({
  rows,
  loading,
  failed,
  newIds,
}: {
  rows: MyFeedbackRow[] | undefined
  loading: boolean
  failed: boolean
  newIds: Set<string>
}) {
  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>
  if (failed) return <p className="text-sm text-danger-600">Couldn't load your feedback.</p>
  if (!rows || rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing yet. When you send feedback, you'll see it here with our reply.
      </p>
    )
  }
  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <li
          key={row.feedback_id}
          data-testid="my-feedback-item"
          className="rounded-md border border-border p-3 text-sm"
        >
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{row.reference}</span>
            <span>{FEEDBACK_TYPE_LABELS[row.type]}</span>
            <span className="rounded border border-border px-1.5 py-0.5 uppercase tracking-wide">
              {FEEDBACK_STATUS_LABELS[row.status] ?? row.status}
            </span>
            {newIds.has(row.feedback_id) && (
              <span className="rounded bg-foreground px-1.5 py-0.5 uppercase tracking-wide text-background">
                New
              </span>
            )}
          </div>
          {row.status === 'closed' && row.resolution && (
            <p className="mt-1 font-medium">{FEEDBACK_RESOLUTION_LABELS[row.resolution]}</p>
          )}
          <p className="mt-1 line-clamp-3 whitespace-pre-wrap">{row.body}</p>
          {row.public_reply && (
            <p className="mt-2 border-l-4 border-border bg-muted px-3 py-2">
              <span className="font-medium">Reply: </span>
              {row.public_reply}
            </p>
          )}
        </li>
      ))}
    </ul>
  )
}
